/* atelier-streaming-text.js
 * <text-stream> custom element. Vanilla JS port of the 10 streaming-text
 * animations from Gallery 53 design handoff (design_handoff_streaming_chat).
 *
 * Attributes:
 *   type        one of typewriter | word-fade | blur-sharp | word-lift |
 *               skeleton-settle | scramble-lock | token-chunks | mask-wipe |
 *               letter-rise | fresh-ink
 *   stream-ms   duration of reveal (default 4800)
 *   hold-ms     pause before loop iteration (default 2400)
 *   loop        "true" | "false" (default false)
 *   trigger     in-view | load | hover | manual (default in-view)
 *
 * Behavior:
 *   - Liquid renders the full answer text inside <div class="resp">…</div>.
 *     That text is the SEO / screen-reader / reduced-motion source of truth.
 *   - On hydrate, JS caches the text, clears the container, and plays the
 *     selected animation. On completion (or under prefers-reduced-motion)
 *     the full text is restored verbatim.
 *   - Emits a `streaming-text:done` CustomEvent (bubbles) when finished.
 */
(function () {
  if (customElements.get('text-stream')) return;

  const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
  const SCRAMBLE_CHARS = 'abcdefghijklmnopqrstuvwxyz';

  /* ----- animation registry: init(resp, text) builds DOM once;
     update(resp, t, streamMs) mutates inline styles each frame. ----- */
  const ANIMS = {
    'typewriter': {
      init(el, text) {
        el.textContent = '';
        el._txt = document.createTextNode('');
        el.appendChild(el._txt);
        const caret = document.createElement('span');
        caret.className = 'atl-sc__caret';
        el.appendChild(caret);
        el._caret = caret;
        el._text = text;
      },
      update(el, t, sms) {
        const p = clamp01(t / sms);
        const eased = 1 - Math.pow(1 - p, 1.25);
        const n = Math.floor(eased * el._text.length);
        el._txt.nodeValue = el._text.slice(0, n);
      },
      finish(el) { if (el._caret) el._caret.remove(); }
    },

    'word-fade': {
      init(el, text) { wordSpans(el, text); },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          el._spans[i].style.opacity = clamp01(wt / 260);
        }
      }
    },

    'blur-sharp': {
      init(el, text) { wordSpans(el, text, { gap: '0.28em' }); },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          const p = clamp01(wt / 420);
          const blur = (1 - p) * 5;
          const s = el._spans[i].style;
          s.filter = 'blur(' + blur.toFixed(2) + 'px)';
          s.opacity = 0.15 + 0.85 * p;
        }
      }
    },

    'word-lift': {
      init(el, text) { wordSpans(el, text, { gap: '0.28em' }); },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          const p = clamp01(wt / 320);
          const eased = 1 - Math.pow(1 - p, 2);
          const ty = (1 - eased) * 8;
          const s = el._spans[i].style;
          s.transform = 'translateY(' + ty.toFixed(2) + 'px)';
          s.opacity = eased;
        }
      }
    },

    'skeleton-settle': {
      init(el, text) {
        el.innerHTML = '';
        el._spans = [];
        const words = text.split(' ');
        words.forEach((w, i) => {
          const wrap = document.createElement('span');
          wrap.className = 'atl-sc__skel-wrap';
          wrap.style.marginRight = '0.28em';

          const bar = document.createElement('span');
          bar.className = 'atl-sc__skel-bar';
          bar.style.width = (w.length * 0.52).toFixed(2) + 'em';
          bar.style.opacity = '0';
          wrap.appendChild(bar);

          const txt = document.createElement('span');
          txt.textContent = w;
          txt.style.opacity = '0';
          wrap.appendChild(txt);

          el.appendChild(wrap);
          el._spans.push({ bar, txt });
        });
      },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          const showSkel = wt > -260 && wt < 260;
          const realP = clamp01((wt - 220) / 220);
          el._spans[i].bar.style.opacity = showSkel ? 1 : 0;
          el._spans[i].txt.style.opacity = realP;
        }
      }
    },

    'scramble-lock': {
      init(el, text) {
        el.innerHTML = '';
        el._chars = [];
        el._text = text;
        for (let i = 0; i < text.length; i++) {
          const s = document.createElement('span');
          s.textContent = text[i] === ' ' ? ' ' : text[i];
          s.style.opacity = '0';
          el.appendChild(s);
          el._chars.push(s);
        }
        el._lastTick = -1;
      },
      update(el, t, sms) {
        const per = sms / el._text.length;
        const tick = Math.floor(t / 55);
        if (tick === el._lastTick) return;
        el._lastTick = tick;
        for (let i = 0; i < el._text.length; i++) {
          const c = el._text[i];
          const lock = i * per;
          const node = el._chars[i];
          if (t >= lock) {
            node.textContent = c === ' ' ? ' ' : c;
            node.style.opacity = '1';
          } else if (t < lock - 700) {
            node.style.opacity = '0';
          } else if (c === ' ') {
            node.textContent = ' ';
            node.style.opacity = '1';
          } else if (!/[a-zA-Z]/.test(c)) {
            node.textContent = c;
            node.style.opacity = '0.5';
          } else {
            const r = SCRAMBLE_CHARS[(tick * 7 + i * 13) % SCRAMBLE_CHARS.length];
            node.textContent = c === c.toUpperCase() ? r.toUpperCase() : r;
            node.style.opacity = '0.55';
          }
        }
      }
    },

    'token-chunks': {
      init(el, text) {
        el.innerHTML = '';
        el._spans = [];
        const tokens = splitTokens(text);
        tokens.forEach((tok) => {
          const s = document.createElement('span');
          s.textContent = tok;
          s.style.opacity = '0';
          el.appendChild(s);
          el._spans.push(s);
        });
      },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          el._spans[i].style.opacity = clamp01(wt / 90);
        }
      }
    },

    'mask-wipe': {
      init(el, text) { wordSpans(el, text, { gap: '0.28em' }); },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          const p = clamp01(wt / 260);
          const insetR = (1 - p) * 100;
          el._spans[i].style.clipPath = 'inset(0 ' + insetR.toFixed(1) + '% -0.2em 0)';
        }
      }
    },

    'letter-rise': {
      init(el, text) {
        el.innerHTML = '';
        el._chars = [];
        el._text = text;
        const parts = text.split(/(\s+)/);
        let globalIdx = 0;
        parts.forEach((part) => {
          if (/^\s+$/.test(part)) {
            el.appendChild(document.createTextNode(part));
            globalIdx += part.length;
            return;
          }
          const wrap = document.createElement('span');
          wrap.style.display = 'inline-block';
          wrap.style.whiteSpace = 'nowrap';
          for (let i = 0; i < part.length; i++) {
            const g = document.createElement('span');
            g.textContent = part[i];
            g.style.display = 'inline-block';
            g.style.transform = 'translateY(6px)';
            g.style.opacity = '0';
            wrap.appendChild(g);
            el._chars.push({ node: g, idx: globalIdx + i });
          }
          el.appendChild(wrap);
          globalIdx += part.length;
        });
      },
      update(el, t, sms) {
        const per = sms / el._text.length;
        for (let i = 0; i < el._chars.length; i++) {
          const { node, idx } = el._chars[i];
          const wt = t - idx * per;
          const p = clamp01(wt / 220);
          const eased = 1 - Math.pow(1 - p, 2);
          node.style.transform = 'translateY(' + ((1 - eased) * 6).toFixed(2) + 'px)';
          node.style.opacity = eased;
        }
      }
    },

    'fresh-ink': {
      init(el, text) {
        el.innerHTML = '';
        el._spans = [];
        const words = text.split(' ');
        words.forEach((w, i) => {
          const s = document.createElement('span');
          s.textContent = w + (i < words.length - 1 ? ' ' : '');
          s.style.opacity = '0';
          el.appendChild(s);
          el._spans.push(s);
        });
      },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          const s = el._spans[i].style;
          if (wt < 0) { s.opacity = '0'; continue; }
          const appear = clamp01(wt / 160);
          const settle = clamp01((wt - 200) / 900);
          const weight = 500 - 200 * settle;
          const ls = 0.01 - 0.01 * settle;
          s.opacity = appear;
          s.fontWeight = weight;
          s.letterSpacing = ls.toFixed(4) + 'em';
        }
      }
    },

    /* ---- v2: 11–20 ---- */

    'diffusion': {
      init(el, text) {
        el.innerHTML = '';
        el._chars = [];
        el._text = text;
        el._lockTimes = [];
        for (let i = 0; i < text.length; i++) {
          const s = document.createElement('span');
          s.textContent = text[i];
          s.style.opacity = '0';
          el.appendChild(s);
          el._chars.push(s);
          const r = Math.sin(i * 91.7 + 17.3) * 43758.5453;
          el._lockTimes.push((r - Math.floor(r)) * 0.92);
        }
        el._lastTick = -1;
      },
      update(el, t, sms) {
        const tick = Math.floor(t / 55);
        if (tick === el._lastTick) return;
        el._lastTick = tick;
        for (let i = 0; i < el._text.length; i++) {
          const c = el._text[i];
          const node = el._chars[i];
          const lock = el._lockTimes[i] * sms;
          if (c === ' ') { node.textContent = ' '; node.style.opacity = '1'; continue; }
          if (!/[a-zA-Z]/.test(c)) { node.textContent = c; node.style.opacity = '1'; continue; }
          if (t >= lock) {
            node.textContent = c;
            node.style.opacity = '1';
          } else {
            const r = SCRAMBLE_CHARS[(tick * 11 + i * 23) % SCRAMBLE_CHARS.length];
            node.textContent = c === c.toUpperCase() ? r.toUpperCase() : r;
            node.style.opacity = '0.5';
          }
        }
      }
    },

    'odometer': {
      init(el, text) {
        el.innerHTML = '';
        el._chars = [];
        el._text = text;
        for (let i = 0; i < text.length; i++) {
          const c = text[i];
          if (c === ' ') {
            const s = document.createElement('span');
            s.innerHTML = '&nbsp;';
            el.appendChild(s);
            el._chars.push({ kind: 'space', node: s });
            continue;
          }
          if (!/[a-zA-Z]/.test(c)) {
            const s = document.createElement('span');
            s.textContent = c;
            s.style.opacity = '0';
            el.appendChild(s);
            el._chars.push({ kind: 'other', node: s });
            continue;
          }
          const wrap = document.createElement('span');
          wrap.className = 'atl-sc__odo';
          const stack = document.createElement('span');
          stack.className = 'atl-sc__odo-stack';
          for (let k = 0; k < 5; k++) {
            const r = SCRAMBLE_CHARS[(i * 13 + k * 31) % SCRAMBLE_CHARS.length];
            const ch = document.createElement('span');
            ch.className = 'atl-sc__odo-ch';
            ch.textContent = c === c.toUpperCase() ? r.toUpperCase() : r;
            stack.appendChild(ch);
          }
          const real = document.createElement('span');
          real.className = 'atl-sc__odo-ch';
          real.textContent = c;
          stack.appendChild(real);
          wrap.appendChild(stack);
          el.appendChild(wrap);
          el._chars.push({ kind: 'roll', node: wrap, stack: stack });
        }
      },
      update(el, t, sms) {
        const per = sms / el._text.length;
        const rollMs = 420;
        for (let i = 0; i < el._text.length; i++) {
          const slot = el._chars[i];
          if (slot.kind === 'space') continue;
          const wt = t - (i * per - rollMs);
          if (slot.kind === 'other') {
            slot.node.style.opacity = wt >= 0 ? '1' : '0';
            continue;
          }
          if (wt < 0) { slot.stack.style.transform = 'translateY(0%)'; continue; }
          if (wt >= rollMs) { slot.stack.style.transform = 'translateY(-500%)'; continue; }
          const p = wt / rollMs;
          const eased = 1 - Math.pow(1 - p, 3);
          slot.stack.style.transform = 'translateY(' + (-eased * 500).toFixed(2) + '%)';
        }
      }
    },

    'ink-bleed': {
      init(el, text) { wordSpans(el, text, { gap: '0.28em' }); },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          const p = clamp01(wt / 460);
          const eased = 1 - Math.pow(1 - p, 2);
          const blur = (1 - eased) * 10;
          const s = el._spans[i].style;
          s.opacity = eased;
          s.textShadow = blur > 0.2
            ? '0 0 ' + blur.toFixed(1) + 'px currentColor, 0 0 ' + (blur * 0.6).toFixed(1) + 'px currentColor'
            : 'none';
        }
      }
    },

    'self-correct': {
      init(el, text) {
        el.innerHTML = '';
        el._txt = document.createTextNode('');
        el.appendChild(el._txt);
        const caret = document.createElement('span');
        caret.className = 'atl-sc__caret';
        el.appendChild(caret);
        el._caret = caret;
        el._text = text;
        const words = text.split(' ');
        el._wrong = words.length < 3
          ? text.slice(0, 16)
          : [words[1], words[0]].concat(words.slice(2, 4)).join(' ');
      },
      update(el, t, sms) {
        const t1 = 1200;
        const t2 = 500;
        const t3 = sms - t1 - t2;
        let display;
        if (t < t1) {
          const p = t / t1;
          display = el._wrong.slice(0, Math.floor(p * el._wrong.length));
        } else if (t < t1 + t2) {
          const p = (t - t1) / t2;
          const remaining = Math.ceil((1 - p) * el._wrong.length);
          display = el._wrong.slice(0, remaining);
        } else {
          const p = clamp01((t - t1 - t2) / t3);
          const eased = 1 - Math.pow(1 - p, 1.2);
          display = el._text.slice(0, Math.floor(eased * el._text.length));
        }
        el._txt.nodeValue = display;
      },
      finish(el) { if (el._caret) el._caret.remove(); }
    },

    'scan-reveal': {
      init(el, text) {
        el.innerHTML = '';
        el._chars = [];
        el._text = text;
        for (let i = 0; i < text.length; i++) {
          const s = document.createElement('span');
          s.textContent = text[i];
          s.style.opacity = '0';
          el.appendChild(s);
          el._chars.push(s);
        }
      },
      update(el, t, sms) {
        const per = sms / el._text.length;
        for (let i = 0; i < el._text.length; i++) {
          const wt = t - i * per;
          const s = el._chars[i].style;
          if (wt < 0) { s.opacity = '0'; s.textShadow = 'none'; s.fontWeight = '400'; continue; }
          const flash = clamp01(1 - wt / 240);
          const op = clamp01(wt / 90);
          s.opacity = op;
          s.textShadow = flash > 0.05
            ? '0 0 ' + (flash * 8).toFixed(1) + 'px currentColor, 0 0 ' + (flash * 14).toFixed(1) + 'px currentColor'
            : 'none';
          s.fontWeight = 400 + flash * 220;
        }
      }
    },

    'redaction-lift': {
      init(el, text) {
        el.innerHTML = '';
        el._words = [];
        const words = text.split(' ');
        words.forEach((w, i) => {
          const wrap = document.createElement('span');
          wrap.className = 'atl-sc__redact';
          wrap.style.marginRight = '0.28em';
          const word = document.createElement('span');
          word.textContent = w;
          word.style.opacity = '0.12';
          wrap.appendChild(word);
          const bar = document.createElement('span');
          bar.className = 'atl-sc__redact-bar';
          wrap.appendChild(bar);
          el.appendChild(wrap);
          el._words.push({ word, bar });
        });
      },
      update(el, t, sms) {
        const per = sms / el._words.length;
        for (let i = 0; i < el._words.length; i++) {
          const wt = t - i * per;
          const p = clamp01(wt / 380);
          const eased = 1 - Math.pow(1 - p, 2);
          el._words[i].word.style.opacity = 0.12 + 0.88 * eased;
          el._words[i].bar.style.opacity = 1 - eased * 0.95;
          el._words[i].bar.style.transform = 'translateY(' + (-eased * 115).toFixed(1) + '%)';
        }
      }
    },

    'stamp': {
      init(el, text) { wordSpans(el, text, { gap: '0.28em' }); },
      update(el, t, sms) {
        const per = sms / el._spans.length;
        for (let i = 0; i < el._spans.length; i++) {
          const wt = t - i * per;
          const s = el._spans[i].style;
          if (wt < 0) { s.opacity = '0'; s.transform = 'scale(1.7)'; continue; }
          const p = clamp01(wt / 360);
          const eased = 1 - Math.pow(1 - p, 3);
          const scale = 1.7 - 0.75 * eased;
          const bounce = p > 0.7 ? Math.sin(((p - 0.7) * Math.PI) / 0.3) * 0.04 : 0;
          s.transform = 'scale(' + (scale + bounce).toFixed(3) + ')';
          s.transformOrigin = 'center';
          s.opacity = eased;
        }
      }
    },

    'wave': {
      init(el, text) {
        el.innerHTML = '';
        el._chars = [];
        el._text = text;
        const parts = text.split(/(\s+)/);
        let globalIdx = 0;
        parts.forEach((part) => {
          if (/^\s+$/.test(part)) {
            el.appendChild(document.createTextNode(part));
            globalIdx += part.length;
            return;
          }
          const wrap = document.createElement('span');
          wrap.style.display = 'inline-block';
          wrap.style.whiteSpace = 'nowrap';
          for (let i = 0; i < part.length; i++) {
            const g = document.createElement('span');
            g.textContent = part[i];
            g.style.display = 'inline-block';
            g.style.opacity = '0';
            wrap.appendChild(g);
            el._chars.push({ node: g, idx: globalIdx + i });
          }
          el.appendChild(wrap);
          globalIdx += part.length;
        });
      },
      update(el, t, sms) {
        const per = sms / el._text.length;
        for (let i = 0; i < el._chars.length; i++) {
          const { node, idx } = el._chars[i];
          const wt = t - idx * per;
          if (wt < 0) { node.style.opacity = '0'; node.style.transform = 'translateY(0)'; continue; }
          const p = clamp01(wt / 700);
          const eased = 1 - Math.pow(1 - p, 2);
          const amp = (1 - eased) * 10;
          const phase = (wt / 600) * Math.PI * 1.6;
          const ty = Math.sin(phase) * amp;
          node.style.transform = 'translateY(' + ty.toFixed(2) + 'px)';
          node.style.opacity = clamp01(wt / 200);
        }
      }
    },

    'marquee-up': {
      init(el, text) {
        el.innerHTML = '';
        el._inners = [];
        const words = text.split(' ');
        words.forEach((w, i) => {
          const wrap = document.createElement('span');
          wrap.className = 'atl-sc__marq';
          const inner = document.createElement('span');
          inner.className = 'atl-sc__marq-inner';
          inner.textContent = w;
          inner.style.transform = 'translateY(100%)';
          wrap.appendChild(inner);
          el.appendChild(wrap);
          el._inners.push(inner);
          if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
        });
      },
      update(el, t, sms) {
        const per = sms / el._inners.length;
        for (let i = 0; i < el._inners.length; i++) {
          const wt = t - i * per;
          const p = clamp01(wt / 420);
          const eased = 1 - Math.pow(1 - p, 3);
          el._inners[i].style.transform = 'translateY(' + ((1 - eased) * 100).toFixed(1) + '%)';
        }
      }
    },

    'magnetic': {
      init(el, text) {
        el.innerHTML = '';
        el._chars = [];
        el._text = text;
        const parts = text.split(/(\s+)/);
        let globalIdx = 0;
        parts.forEach((part) => {
          if (/^\s+$/.test(part)) {
            el.appendChild(document.createTextNode(part));
            globalIdx += part.length;
            return;
          }
          const wrap = document.createElement('span');
          wrap.style.display = 'inline-block';
          wrap.style.whiteSpace = 'nowrap';
          for (let i = 0; i < part.length; i++) {
            const idx = globalIdx + i;
            const g = document.createElement('span');
            g.textContent = part[i];
            g.style.display = 'inline-block';
            g.style.opacity = '0';
            const r1 = Math.sin(idx * 12.9898) * 43758.5453;
            const r2 = Math.cos(idx * 78.233) * 12345.678;
            const x = ((r1 - Math.floor(r1)) - 0.5) * 44;
            const y = ((r2 - Math.floor(r2)) - 0.5) * 28 - 6;
            wrap.appendChild(g);
            el._chars.push({ node: g, idx: idx, x: x, y: y });
          }
          el.appendChild(wrap);
          globalIdx += part.length;
        });
      },
      update(el, t, sms) {
        const per = sms / el._text.length;
        for (let i = 0; i < el._chars.length; i++) {
          const { node, idx, x, y } = el._chars[i];
          const wt = t - idx * per;
          const p = clamp01(wt / 460);
          const eased = 1 - Math.pow(1 - p, 2.4);
          node.style.transform = 'translate(' + (x * (1 - eased)).toFixed(2) + 'px, ' + (y * (1 - eased)).toFixed(2) + 'px)';
          node.style.opacity = eased;
        }
      }
    }
  };

  /* ----- shared helpers ----- */
  function wordSpans(el, text, opts) {
    opts = opts || {};
    el.innerHTML = '';
    el._spans = [];
    const words = text.split(' ');
    words.forEach((w, i) => {
      const s = document.createElement('span');
      if (opts.gap) {
        s.textContent = w;
        s.style.display = 'inline-block';
        s.style.marginRight = opts.gap;
      } else {
        s.textContent = w + (i < words.length - 1 ? ' ' : '');
      }
      s.style.opacity = '0';
      el.appendChild(s);
      el._spans.push(s);
    });
  }

  function splitTokens(text) {
    const out = [];
    const parts = text.split(/(\s+)/);
    for (const w of parts) {
      if (/^\s+$/.test(w)) { out.push(w); continue; }
      if (w.length <= 4) { out.push(w); continue; }
      let i = 0;
      while (i < w.length) {
        const n = 2 + ((i + w.length) % 3);
        out.push(w.slice(i, i + n));
        i += n;
      }
    }
    return out;
  }

  /* ----- custom element ----- */
  class TextStream extends HTMLElement {
    constructor() {
      super();
      this._raf = 0;
      this._observer = null;
      this._startTime = 0;
      this._done = false;
      this._anim = null;
      this._resp = null;
      this._finalText = '';
    }

    connectedCallback() {
      this._streamMs = parseInt(this.getAttribute('stream-ms'), 10) || 4800;
      this._holdMs = parseInt(this.getAttribute('hold-ms'), 10) || 5400;
      this._loopMs = this._streamMs + this._holdMs;
      this._loop = this.getAttribute('loop') === 'true';
      this._trigger = this.getAttribute('trigger') || 'in-view';
      const type = this.getAttribute('type') || 'typewriter';

      this._resp = this.querySelector('.resp');
      if (!this._resp) return;
      this._finalText = (this._resp.textContent || '').trim();
      if (!this._finalText) return;

      this._anim = ANIMS[type] || ANIMS.typewriter;

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this._anim.init(this._resp, this._finalText);

      switch (this._trigger) {
        case 'load':
          this.play();
          break;
        case 'hover':
          this.addEventListener('pointerenter', this._playOnce.bind(this), { once: true });
          break;
        case 'manual':
          break;
        default:
          this._setupInView();
      }
    }

    disconnectedCallback() {
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._observer) this._observer.disconnect();
    }

    _setupInView() {
      if (!('IntersectionObserver' in window)) { this.play(); return; }
      this._observer = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.3) {
            this.play();
            this._observer.disconnect();
            this._observer = null;
            break;
          }
        }
      }, { threshold: [0, 0.3, 0.6, 1] });
      this._observer.observe(this);
    }

    _playOnce() { this.play(); }

    play() {
      if (this._raf || !this._anim) return;
      this._startTime = performance.now();
      this._done = false;
      const tick = (now) => {
        const elapsed = now - this._startTime;
        let t;
        if (this._loop) {
          t = elapsed % this._loopMs;
        } else {
          t = elapsed > this._streamMs ? this._streamMs : elapsed;
        }
        this._anim.update(this._resp, t, this._streamMs);
        if (!this._loop && elapsed >= this._streamMs) {
          this._finish();
          return;
        }
        this._raf = requestAnimationFrame(tick);
      };
      this._raf = requestAnimationFrame(tick);
    }

    _finish() {
      this._raf = 0;
      this._done = true;
      if (this._anim.finish) this._anim.finish(this._resp);
      this._resp.textContent = this._finalText;
      this.dispatchEvent(new CustomEvent('streaming-text:done', { bubbles: true }));
    }
  }

  customElements.define('text-stream', TextStream);
})();
